import * as d3 from 'd3';
import * as moment from 'moment';
import { TDrawModel } from '../../draw.model';
import { TTimelineChartGroups } from '../draw';
import { DRAW_ELEMENT } from './elements';
import { TEntityActionCb, TTimelineModel } from '../../timeline.types';
import { DRAW_MODEL_UTILS } from '../utils';

const stripToolTip = (chartTemplate: TTimelineChartGroups, drawModel: TDrawModel) => {
  chartTemplate.elements
    .on('mousemove.tooltip', function (ctx, d) {
      const [x, y] = d3.pointer(ctx, document.body);

      DRAW_ELEMENT.addToolTip(drawModel, x, y)
        .style('left', x + 'px')
        .style('top', `${y}px`)
        .style('transform', () => {
          const xShift = x > window.innerWidth * (70 / 100) ? `calc(-100% + 15px)` : 0;
          const yShift = y > window.innerHeight * (70 / 100) ? `-100%` : `20px`;
          return `translate(${xShift}, ${yShift})`;
        })
        .style('max-width', '150px')
        .style('width', 'max-content')
        .attr('fill', 'black')
        .html(() => {
          switch (d.type) {
            case 'STRIP': {
              const iconListHtml = DRAW_MODEL_UTILS.getTooltipHTMLElements(d.iconList);
              return d.isStripOutdated
                ? `
                  <div> Order Items Updated. </br> Please <b> Re-plan </b> </div>
                  `
                : `
                  <div> ${iconListHtml} </div>
                  <b> ${d.name ? d.name + '</b></br>' : ''}
                  <b> ${d.additionalInfo ? d.additionalInfo + '</b></br>' : ''}
                  From: ${moment(d.range.startDate).format('ll')} </br>
                  To: ${moment(d.range.endDate).format('ll')}
                  ${d.note ? '</br> Note: ' + d.note : ''}
                  `;
            }
            default:
              return `
                  <b>${d.name ? d.name + '</b></br>' : ''}
                  ${moment(d.range.startDate).format('ll')} </br>
                  `;
          }
        });
    })
    .on('mouseout.tooltip', function () {
      DRAW_ELEMENT.removeToolTip();
    });

  chartTemplate.entityName
    ?.on('mousemove.tooltip', function (ctx, d) {
      const [x, y] = d3.pointer(ctx, document.body);
      DRAW_ELEMENT.addToolTip(drawModel, x, y)
        .style('left', x + 'px')
        .style('top', `${y + 20}px`)
        .style('transform', x > window.innerWidth / 2 ? `translate(-90%, 0)` : '')
        .style('min-width', '150px')
        .attr('fill', 'black')
        .html(() => `<b>${d.name.displayTxt}</b>`);
    })
    .on('mouseout.tooltip', function () {
      DRAW_ELEMENT.removeToolTip();
    });

  chartTemplate.leftCount
    ?.on('mousemove.tooltip', function (ctx, d) {
      const [x, y] = d3.pointer(ctx, document.body);
      // const boundRect = self.querySelector('.elm')?.getBoundingClientRect() as DOMRect;
      DRAW_ELEMENT.addToolTip(drawModel, x, y)
        .style('left', x + 'px')
        .style('top', `${y + 20}px`)
        .style('transform', x > window.innerWidth / 2 ? `translate(-90%, 0)` : '')
        .style('min-width', '150px')
        .attr('fill', 'black')
        .html(() => `<b>Number of Strips</b>`);
    })
    .on('mouseout.tooltip', function () {
      DRAW_ELEMENT.removeToolTip();
    });
  chartTemplate.rightCount
    ?.on('mousemove.tooltip', function (ctx, d) {
      const [x, y] = d3.pointer(ctx, document.body);
      DRAW_ELEMENT.addToolTip(drawModel, x, y)
        .style('left', x + 'px')
        .style('top', `${y + 20}px`)
        .style('transform', x > window.innerWidth / 2 ? `translate(-90%, 0)` : '')
        .style('min-width', '150px')
        .attr('fill', 'black')
        .html(() => `<b>Number of Operators</b>`);
    })
    .on('mouseout.tooltip', function () {
      DRAW_ELEMENT.removeToolTip();
    });

  chartTemplate.entityTitle
    ?.on('mousemove.tooltip', function (ctx, d) {
      const titleOpt = d.metadata?.title;
      if (!titleOpt) return;
      const [x, y] = d3.pointer(ctx, document.body);
      DRAW_ELEMENT.addToolTip(drawModel, x, y)
        .style('left', x + 'px')
        .style('top', `${y + 20}px`)
        .style('transform', x > window.innerWidth / 2 ? `translate(-90%, 0)` : '')
        .style('min-width', '150px')
        .attr('fill', 'black')
        .html(() => `<b>${titleOpt}</b>`);
    })
    .on('mouseout.tooltip', function () {
      DRAW_ELEMENT.removeToolTip();
    });

  chartTemplate.entityIcon
    ?.on('mousemove.tooltip', function (ctx, d) {
      const [x, y] = d3.pointer(ctx, document.body);
      DRAW_ELEMENT.addToolTip(drawModel, x, y)
        .style('left', x + 'px')
        .style('top', `${y + 20}px`)
        .style('transform', x > window.innerWidth / 2 ? `translate(-90%, 0)` : '')
        .style('min-width', '150px')
        .attr('fill', 'black')
        .html(() => `<b>${d.metadata?.icon?.tooltip}</b>`);
    })
    .on('mouseout.tooltip', function () {
      DRAW_ELEMENT.removeToolTip();
    });
};

const stripLeftClick = (chartTemplate: TTimelineChartGroups, stripFn: TEntityActionCb) => {
  chartTemplate.elements.on('click.left_click', function (e, stripInfo) {
    const self = this as unknown as any;
    const entityInfo = d3.select(self?.parentNode).datum() as TTimelineModel['entities'][number]['entities'][number];
    const grouEngityData = d3.select(self?.parentNode?.parentNode).datum() as TTimelineModel['entities'][number];
    if (stripInfo.data) {
      stripFn({
        type: 'STRIP',
        action: 'LEFT_CLICK',
        event: e,
        entityData: entityInfo.data,
        elementData: stripInfo.data,
        entityGroupData: grouEngityData.data,
      });
    }
  });
};

const stripRightClick = (chartTemplate: TTimelineChartGroups, showMore: TEntityActionCb) => {
  chartTemplate.elements.on('contextmenu.right_click', function (e, stripInfo) {
    e.preventDefault();
    const self = this as unknown as any;
    const entityInfo = d3.select(self?.parentNode).datum() as TTimelineModel['entities'][number]['entities'][number];
    const grouEngityData = d3.select(self?.parentNode?.parentNode).datum() as TTimelineModel['entities'][number];
    if (stripInfo.data) {
      showMore({
        type: 'STRIP',
        action: 'RIGHT_CLICK',
        event: e,
        entityData: entityInfo.data,
        elementData: stripInfo.data,
        entityGroupData: grouEngityData.data,
      });
    }
  });
};

export const CHART_EVENTS = {
  stripLeftClick,
  stripRightClick,
  stripToolTip,
};
